import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useState } from 'react';

import { useAuthorizeSigningRequest, usePopup } from '@/providers';
import { InfinexLogoTilePath } from '@infinex-private/connect-web-shared/assets';
import {
  FullscreenContainer,
  AppInfoCard,
  MoreDetailsButton,
  UserInfoCard,
} from '@infinex-private/connect-web-shared/components';
import { PasskeyIcon, SendFillIcon, EditIcon } from '@infinex/ui-icons';
import { Button, Text, DialogMarshal } from '@infinex/ui/components';
import { GasDialog, useGasInfo } from '@infinex/web-shared/components';
import { useIsPatron } from '@infinex/web-shared/hooks';
import { EXTERNAL_ROUTES } from '@infinex/web-shared/lib';
import {
  getCustodyIcon,
  getExternalSignerIconType,
} from '@infinex/web-shared/utils';

import { EditApprovalAmountDialog } from './EditApprovalAmountDialog';
import { GasDetailsSection } from './GasDetailsSection';
import { useGasDetailsUserInfo } from './GasDetailsSection/hooks/useGasDetailsUserInfo';
import { HardwareWalletSigningView } from './HardwareWalletSigningView';
import {
  extractRawMessage,
  getMaliciousDescription,
  getNormalizedValidationResult,
} from './helpers/signingRequestHelpers';
import { MaliciousTransactionDetails } from './MaliciousTransactionDetails';
import { TransactionRequestInfo } from './TransactionRequestInfo';
import { ValidationResultCard } from './ValidationResultCard';

import { useApprovalEditing } from '../hooks/useApprovalEditing';
import type { ChainValidationResponse } from '../types';

type SigningRequestLayoutProps = {
  isTransaction?: boolean;
};

export function SigningRequestLayout({
  isTransaction = false,
}: SigningRequestLayoutProps) {
  const { popupData } = usePopup() as { popupData: ChainValidationResponse };
  const {
    authorize,
    isAuthorizing,
    metadata,
    selectedSponsorshipMode,
    isHardwareWallet,
    hardwareWalletDeviceType,
    hardwareWalletState,
    hardwareWalletError,
    retryHardwareWallet,
    errorMessage,
    modifications,
    setModification,
  } = useAuthorizeSigningRequest();
  const { dappInfo, selectedWallet, chainKey } = popupData;

  useEffect(() => {
    if (metadata) {
      datadogRum.setGlobalContextProperty('metadata', metadata);
    }
  }, [metadata]);

  const walletAddress =
    chainKey === 'solana'
      ? (selectedWallet.wallets.solana ?? '')
      : (selectedWallet.wallets.evm ?? '');

  const gasDetails = useGasDetailsUserInfo({
    chainKey,
    walletAddress,
    selectedSponsorshipMode,
  });
  const [showTransactionInfo, setShowTransactionInfo] = useState(false);
  const [showMaliciousDetails, setShowMaliciousDetails] = useState(false);
  const [isGasDialogOpen, setIsGasDialogOpen] = useState(false);

  const gasInfo = useGasInfo();
  const isPatron = useIsPatron();

  // Hardware wallets always pay gas with native ETH, never gas account
  const isGasAccountSupported =
    !isHardwareWallet &&
    (metadata?.supportedSponsorshipModes?.includes('account') ?? false);

  const rawMessage = isTransaction ? undefined : extractRawMessage(metadata);

  const normalizedValidationResult = getNormalizedValidationResult(
    metadata,
    chainKey
  );

  const maliciousDescription = getMaliciousDescription(
    metadata?.blockaidValidationResult
  );

  const isGasPayableByUser =
    isTransaction &&
    (isHardwareWallet ||
      !metadata?.supportedSponsorshipModes?.includes('sponsored'));

  const isGasMetadataLoading =
    !metadata?.quoteGasDetails || !metadata?.quoteGasCostUsd;

  const isLoadingGasInfo =
    isGasPayableByUser && (gasDetails.isLoadingBalance || isGasMetadataLoading);

  const inputType = metadata?.input?.type;
  const isSvmHardwareWalletSignMessage =
    isHardwareWallet && inputType === 'svm:signMessage';
  const isSvmHardwareWalletBatchTransactions =
    isHardwareWallet && inputType === 'svm:batchTransactions';

  const shouldDisableSign =
    isSvmHardwareWalletSignMessage ||
    isSvmHardwareWalletBatchTransactions ||
    isLoadingGasInfo ||
    (isGasPayableByUser && !gasDetails.hasSufficientFunds);

  const showGasSection =
    metadata?.supportedSponsorshipModes && isGasPayableByUser;

  const {
    isApprovalEditable,
    approvalEditState,
    editingApproval,
    editingApprovalRawAmount,
    editingApprovalUserBalance,
    editingApprovalTokenUsdRate,
    isTokenPriceLoading,
    handleApprovalEditPress,
    handleApprovalEditConfirm,
    dismissEditDialog,
  } = useApprovalEditing({
    metadata,
    chainKey,
    isTransaction,
    isHardwareWallet,
    normalizedValidationResult,
    modifications,
    setModification,
  });

  if (showTransactionInfo) {
    return (
      <TransactionRequestInfo
        hostname={dappInfo.hostname}
        verified={dappInfo.isVerified}
        onBack={() => setShowTransactionInfo(false)}
        isTransaction={isTransaction}
        validationResult={normalizedValidationResult}
        maliciousDescription={maliciousDescription}
        chainKey={chainKey}
      />
    );
  }

  if (showMaliciousDetails) {
    return (
      <MaliciousTransactionDetails
        onBack={() => setShowMaliciousDetails(false)}
        description={maliciousDescription}
      />
    );
  }

  if (
    isHardwareWallet &&
    hardwareWalletDeviceType &&
    hardwareWalletState !== 'idle'
  ) {
    return (
      <HardwareWalletSigningView
        deviceType={hardwareWalletDeviceType}
        state={hardwareWalletState}
        error={hardwareWalletError}
        onCancel={() => window.close()}
        onRetry={retryHardwareWallet}
      />
    );
  }

  return (
    <>
      <FullscreenContainer className="bg-canvas size-full rounded-none">
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-8 py-4">
            <div className="relative">
              <AppInfoCard
                dappIcon={dappInfo.icon}
                dappName={dappInfo.name}
                hostname={dappInfo.hostname}
                dappType={dappInfo.isVerified ? 'verified' : 'unverified'}
                showTopIcon={false}
                subText=""
                className="gap-1 py-8"
              />

              <MoreDetailsButton
                className="pointer-events-auto absolute top-full left-1/2 z-10 -translate-1/2"
                onClick={() => setShowTransactionInfo(true)}
                text={isTransaction ? 'Transaction Request' : 'Signing Request'}
                icon={
                  <>
                    {isTransaction ? (
                      <SendFillIcon fill="brand" size="xsmall" />
                    ) : (
                      <EditIcon fill="brand" size="xsmall" />
                    )}
                  </>
                }
              />
            </div>

            <ValidationResultCard
              chainKey={chainKey}
              txnValidationResult={metadata?.blockaidValidationResult}
              rawMessage={rawMessage}
              title="Summary"
              onMaliciousClick={() => setShowMaliciousDetails(true)}
              isApprovalEditable={isApprovalEditable}
              approvalEditState={approvalEditState}
              onApprovalEditPress={handleApprovalEditPress}
            />

            <UserInfoCard
              emblemSrc={InfinexLogoTilePath}
              walletLabel={selectedWallet.label}
              walletAddress={walletAddress || 'Your address'}
              chainKey={chainKey}
            />

            {showGasSection ? (
              <GasDetailsSection
                userBalance={gasDetails.userBalance}
                isLoadingBalance={
                  gasDetails.isLoadingBalance || isGasMetadataLoading
                }
                gasAccountBalance={gasDetails.gasAccountBalance}
                formattedGasCost={gasDetails.formattedGasCost}
                isHighGasCost={gasDetails.isHighGasCost}
                nativeBalanceUsd={gasDetails.nativeBalanceUsd}
                formattedNativeBalance={gasDetails.formattedNativeBalance}
                hasSufficientFunds={gasDetails.hasSufficientFunds}
                onTopUpGasAccount={
                  isGasAccountSupported
                    ? () => setIsGasDialogOpen(true)
                    : undefined
                }
              />
            ) : null}
          </div>

          <footer className="mt-auto border-t backdrop-blur-sm">
            {maliciousDescription ? (
              <div className="gap-3 px-8 py-4">
                <Button
                  prominence="default"
                  size="large"
                  onPress={() => window.close()}
                  className="mb-2 w-full"
                >
                  Cancel
                </Button>
                <Button
                  prominence="low"
                  size="large"
                  onPress={() => authorize()}
                  className="w-full"
                  isPending={isAuthorizing}
                  isDisabled={shouldDisableSign}
                >
                  <div className="flex items-center gap-1">
                    <PasskeyIcon size="xsmall" />
                    <Text>Proceed</Text>
                  </div>
                </Button>
              </div>
            ) : (
              <div className="gap-3 px-8 py-4">
                {isSvmHardwareWalletSignMessage ||
                isSvmHardwareWalletBatchTransactions ? (
                  <div
                    className="bg-fillCautionEmphasis/10 mb-3 rounded-lg p-3"
                    role="alert"
                  >
                    <Text className="text-caution text-sm">
                      Solana{' '}
                      {isSvmHardwareWalletSignMessage
                        ? 'message'
                        : 'batch transaction'}{' '}
                      signing is not supported by{' '}
                      {hardwareWalletDeviceType === 'ledger'
                        ? 'Ledger'
                        : hardwareWalletDeviceType === 'trezor'
                          ? 'Trezor'
                          : 'hardware wallets'}
                      . Connect your Infinex wallet to sign messages.
                    </Text>
                  </div>
                ) : errorMessage ? (
                  <div className="bg-fillCriticalEmphasis/10 mb-3 rounded-lg p-3">
                    <Text className="text-critical text-sm">
                      {errorMessage}
                    </Text>
                  </div>
                ) : null}

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    prominence="default"
                    size="large"
                    onPress={() => window.close()}
                  >
                    Cancel
                  </Button>
                  <Button
                    prominence="high"
                    size="large"
                    onPress={() => authorize()}
                    isPending={isAuthorizing}
                    isDisabled={shouldDisableSign}
                  >
                    <div className="flex items-center gap-1">
                      {isHardwareWallet ? (
                        (() => {
                          const Icon = getCustodyIcon(
                            getExternalSignerIconType(hardwareWalletDeviceType)
                          );
                          return <Icon size="xsmall" />;
                        })()
                      ) : (
                        <PasskeyIcon size="xsmall" />
                      )}
                      <Text>{isTransaction ? 'Confirm' : 'Sign'}</Text>
                    </div>
                  </Button>
                </div>
              </div>
            )}
          </footer>
        </div>
      </FullscreenContainer>

      {isGasDialogOpen ? (
        <DialogMarshal onDismiss={() => setIsGasDialogOpen(false)}>
          <GasDialog
            {...gasInfo}
            requiresSessionKey={true}
            networkCostsLegalUrl={EXTERNAL_ROUTES.legals.networkCosts}
            isPatron={isPatron}
            topUpType="jit_top_up"
          />
        </DialogMarshal>
      ) : null}

      {editingApproval ? (
        <DialogMarshal onDismiss={dismissEditDialog}>
          <EditApprovalAmountDialog
            approval={editingApproval}
            rawApprovalAmount={editingApprovalRawAmount}
            userBalance={editingApprovalUserBalance.formatted}
            tokenUsdRate={editingApprovalTokenUsdRate}
            isTokenUsdRateLoading={isTokenPriceLoading}
            onConfirm={handleApprovalEditConfirm}
          />
        </DialogMarshal>
      ) : null}
    </>
  );
}
